import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Box } from '@mui/system';
import { useForm } from 'react-hook-form';
import { url } from 'src/Helpers/utils/Constant';
import { useAuthContext } from 'src/components/Context/AuthContext/AuthContext';
import { LoadingButton } from '@mui/lab';
import { IsingleMemberMeeting } from 'src/Types/module/meeting/singleMemberMeeting';
import {
  DesktopDatePicker,
  LocalizationProvider,
  TimePicker,
} from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import CommonMultipleSelectMembers from 'src/common/CommonMultipleSelectMembers';
import UpdateDataApi from 'src/common/FetchData/UpdateDataApi';
import SelectGroupMemberList from 'src/Types/module/invoice/group/SelectGroupMemberList';

function MeetingUpdateForm({
  singleInvoiceDetails,
  handleEditModeChange,
}: {
  singleInvoiceDetails: IsingleMemberMeeting;
  handleEditModeChange: any;
}) {
  const { register, handleSubmit, watch } = useForm();
  const member_meeting_status = watch('member_meeting_status');
  const { user } = useAuthContext();
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(
    dayjs(singleInvoiceDetails?.member_meeting_date)
  );
  const [timeEnd, setTimeEndValue] = useState<Dayjs | null>(() => {
    const date = singleInvoiceDetails?.member_meeting_date;
    const time = singleInvoiceDetails?.member_meeting_end_time;

    if (date && time) {
      // Combine date and time in a standard format
      const dateTimeString = `${date.split('T')[0]}T${time}`;
      return dayjs(dateTimeString);
    } else {
      return null;
    }
  });
  const [timeValue, setTimeValue] = useState<Dayjs | null>(() => {
    const date = singleInvoiceDetails?.member_meeting_date;
    const time = singleInvoiceDetails?.member_meeting_time;

    if (date && time) {
      // Combine date and time in a standard format
      const dateTimeString = `${date.split('T')[0]}T${time}`;
      return dayjs(dateTimeString);
    } else {
      return null;
    }
  });
  const [meetingType, setMeetingType] = useState(
    singleInvoiceDetails.member_meeting_type
  );
  const selectedMembers = useMemo(() => {
    return singleInvoiceDetails?.joinedMember?.map((member) => ({
      user_member_id: member.joined_member_id,
      user_member_company_name: member.user_member_company_name,
    }));
  }, [singleInvoiceDetails]);

  const [membersId, setMembersId] = useState<number[]>([]);
  const [groupsId, setGroupId] = useState<number[]>([]);
  const [removeGroupsId, setRemoveGroupId] = useState<number[]>([]);
  const [removeMemberId, setRemoveMembersId] = useState<number[]>([]);

  const { updateData, success, loading } = UpdateDataApi(
    `${url}/api/member/meeting/update/for-admin/${singleInvoiceDetails?.member_meeting_id}`
  );
  const onSubmit = async (inputs: React.ChangeEvent<HTMLInputElement>) => {
    if (
      user.user_admin_id &&
      singleInvoiceDetails.member_meeting_time &&
      singleInvoiceDetails.member_meeting_date
    ) {
      const bodyFormData = new FormData();

      for (const key in inputs) {
        if (typeof inputs[key] === 'object') {
          if (inputs[key][0]) {
            bodyFormData.append(key, inputs[key][0]);
          }
        } else {
          bodyFormData.append(key, inputs[key]);
        }
      }
      bodyFormData.append(
        'member_meeting_time',
        dayjs(timeValue).format('HH:mm')
      );

      if (timeEnd) {
        bodyFormData.append(
          'member_meeting_end_time',
          dayjs(timeEnd).format('HH:mm')
        );
      }
      bodyFormData.append(
        'member_meeting_date',
        dayjs(selectedDate).format('YYYY-MM-DD')
      );
      bodyFormData.append(
        'member_meeting_id',
        singleInvoiceDetails.member_meeting_id.toString()
      );
      if (meetingType) bodyFormData.append('member_meeting_type', meetingType);

      if (membersId?.length)
        bodyFormData.append('members_id', JSON.stringify(membersId));
      if (removeMemberId?.length)
        bodyFormData.append('remove_members', JSON.stringify(removeMemberId));
      if (groupsId?.length)
        bodyFormData.append('add_groups', JSON.stringify(groupsId));
      if (removeGroupsId?.length)
        bodyFormData.append('remove_groups', JSON.stringify(removeGroupsId));

      await updateData(bodyFormData);
    }
  };

  useEffect(() => {
    if (success) {
      handleEditModeChange();
    }
  }, [success]);
  return (
    <Box
      component='form'
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      autoComplete='off'
    >
      <Grid container spacing={{ xs: 2, md: 2 }}>
        <Grid xs={12} md={4} lg={4} item>
          <TextField
            fullWidth
            label='Member meeting title'
            id='filled-size-small'
            defaultValue={singleInvoiceDetails.member_meeting_title}
            variant='outlined'
            size='medium'
            type='text'
            {...register('member_meeting_title')}
          />
        </Grid>
        <Grid xs={12} md={4} lg={4} item>
          <FormControl required fullWidth>
            <InputLabel required id='demo-simple-select-label'>
              Status
            </InputLabel>
            <Select
              fullWidth
              // sx={{ mt: -2 }}
              labelId='demo-simple-select-label'
              id='demo-simple-select'
              label='Status'
              key={singleInvoiceDetails.member_meeting_status}
              defaultValue={singleInvoiceDetails.member_meeting_status}
              {...register('member_meeting_status')}
            >
              <MenuItem value='upcoming' disabled>
                Upcoming
              </MenuItem>
              <MenuItem value='running'>Running</MenuItem>
              <MenuItem value='ended'>Ended</MenuItem>
              <MenuItem value='cancelled'>Cancelled</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid xs={12} md={4} lg={4} item>
          <FormControl required fullWidth>
            <InputLabel required id='demo-simple-select-label'>
              Meeting type
            </InputLabel>
            <Select
              fullWidth
              // sx={{ mt: -2 }}
              labelId='demo-simple-select-label'
              id='demo-simple-select'
              label='Meeting type'
              value={meetingType}
              onChange={(e) => setMeetingType(e.target.value)}
            >
              <MenuItem value={'all'}>All</MenuItem>
              <MenuItem value={'specific'}>Specific</MenuItem>
              <MenuItem value={'group'}>Group</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        {meetingType === 'specific' && (
          <Grid xs={12} md={12} lg={12} item>
            <CommonMultipleSelectMembers
              onChange={(e) => {}}
              onAddValue={(e) => {
                setMembersId(e);
              }}
              onRemoveValue={(e) => {
                setRemoveMembersId(e);
              }}
              label='Select Members'
              size='medium'
              defaultData={selectedMembers}
            />
          </Grid>
        )}
        {meetingType === 'group' && (
          <Grid xs={12} md={12} lg={12} item>
            <SelectGroupMemberList
              onChange={(e) => {}}
              onAddValue={(e) => {
                setGroupId(e);
              }}
              onRemoveValue={(e) => {
                setRemoveGroupId(e);
              }}
              label='Select Group List'
              size='medium'
              defaultData={singleInvoiceDetails?.groups}
            />
          </Grid>
        )}
        <Grid xs={12} md={4} lg={4} item>
          <TextField
            fullWidth
            label='Member meeting place:'
            id='filled-size-small'
            defaultValue={singleInvoiceDetails.member_meeting_place}
            variant='outlined'
            size='medium'
            type='text'
            {...register('member_meeting_place')}
          />
        </Grid>
        <Grid xs={12} md={4} lg={4} item>
          <TextField
            fullWidth
            id='filled-size-small'
            variant='outlined'
            type='file'
            label='Upload a meeting image'
            InputLabelProps={{
              shrink: true,
            }}
            {...register('member_meeting_img')}
          />
        </Grid>
        <Grid xs={12} md={4} lg={4} item>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DesktopDatePicker
              label='Update meeting date'
              format='MM/DD/YYYY'
              value={selectedDate}
              sx={{ width: '100%' }}
              onChange={(e) => setSelectedDate(e)}
            />
          </LocalizationProvider>
        </Grid>{' '}
        <Grid xs={12} md={4} lg={4} item>
          {' '}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <TimePicker
              label='Start Time'
              value={timeValue}
              sx={{ width: '100%' }}
              onChange={(e: any) => setTimeValue(e)}
            />
          </LocalizationProvider>
        </Grid>
        <Grid xs={12} md={4} lg={4} item>
          {member_meeting_status === 'ended' && (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <TimePicker
                label='End Time'
                value={timeEnd}
                sx={{ width: '100%' }}
                onChange={(e: any) => setTimeEndValue(e)}
              />
            </LocalizationProvider>
          )}
        </Grid>
        <Grid xs={12} md={6} lg={6} item>
          <TextField
            multiline
            rows={4}
            fullWidth
            label='Member meeting details'
            id='filled-size-small'
            defaultValue={singleInvoiceDetails.member_meeting_details}
            variant='outlined'
            size='medium'
            type='text'
            {...register('member_meeting_details')}
          />
        </Grid>
        <Grid xs={12} md={6} lg={6} item>
          <TextField
            label='Member meeting remark:'
            id='filled-size-small'
            multiline
            fullWidth
            rows={4}
            defaultValue={singleInvoiceDetails?.member_meeting_remark}
            variant='outlined'
            size='medium'
            type='text'
            {...register('member_meeting_remark')}
          />
        </Grid>
      </Grid>
      <Box sx={{ textAlign: 'end', mt: 5 }}>
        {' '}
        {!loading ? (
          <Button
            type='submit'
            variant='contained'
            disableElevation
            sx={{
              margin: 2,
            }}
          >
            Update meeting
          </Button>
        ) : (
          <LoadingButton
            loading
            loadingIndicator='Updating...'
            variant='outlined'
          >
            Updating...
          </LoadingButton>
        )}
      </Box>
    </Box>
  );
}

export default MeetingUpdateForm;
